import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useConsentPreferences, ConsentPreferences } from "@/hooks/useConsentPreferences";
import { Cookie, Shield, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

interface CookieSettingsDialogProps {
  trigger?: React.ReactNode;
}

export function CookieSettingsDialog({ trigger }: CookieSettingsDialogProps) {
  const {
    preferences,
    hasConsented,
    isMinor,
    isCoppaAge,
    savePreferences,
  } = useConsentPreferences();

  const [isOpen, setIsOpen] = useState(false);
  const [localPreferences, setLocalPreferences] = useState<ConsentPreferences>({
    essential_cookies: true,
    analytics_cookies: false,
    marketing_cookies: false,
    personalization_cookies: false,
    third_party_sharing: false,
    jurisdiction: "us",
  });

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleSave = async () => {
    await savePreferences(localPreferences);
    setIsOpen(false);
  };

  const togglePreference = (key: keyof ConsentPreferences) => {
    if (key === "essential_cookies") return;
    if (key === "marketing_cookies" && isCoppaAge) return;
    if (key === "third_party_sharing" && isMinor) return;

    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const cookieCategories = [
    {
      key: "essential_cookies" as const,
      label: "Essential Cookies",
      description: "Required for basic site functionality. These cannot be disabled.",
      examples: "Authentication, security, accessibility preferences",
      required: true,
    },
    {
      key: "analytics_cookies" as const,
      label: "Analytics Cookies",
      description: "Help us understand how you use CastReady to improve your experience.",
      examples: "Page views, feature usage, error tracking",
      required: false,
    },
    {
      key: "marketing_cookies" as const,
      label: "Marketing Cookies",
      description: "Allow personalized content recommendations.",
      examples: "Content recommendations, email preferences",
      required: false,
      disabled: isCoppaAge,
      disabledReason: "Not available for users under 13",
    },
    {
      key: "personalization_cookies" as const,
      label: "Personalization Cookies",
      description: "Remember your preferences and customize your experience.",
      examples: "Theme preferences, dashboard layouts, recent activity",
      required: false,
    },
    {
      key: "third_party_sharing" as const,
      label: "Third-Party Data Sharing",
      description: "Share anonymized data with trusted partners for improved services.",
      examples: "Performance optimization, security services",
      required: false,
      disabled: isMinor,
      disabledReason: "Not available for users under 18",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm">
            <Cookie className="h-4 w-4 mr-2" />
            Cookie Settings
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-primary" />
            Cookie & Privacy Settings
          </DialogTitle>
          <DialogDescription>
            Manage how we use cookies and data on CastReady. Your preferences are saved and respected across all platforms.
          </DialogDescription>
        </DialogHeader>

        {isMinor && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Shield className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Youth Protection Active</p>
              <p className="text-xs text-amber-700">
                Some options are limited to protect your privacy.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4 py-2">
          {cookieCategories.map((category, index) => (
            <div key={category.key}>
              {index > 0 && <Separator className="my-4" />}
              <div className={cn(
                "flex items-start justify-between gap-4",
                category.disabled && "opacity-50"
              )}>
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <Label className="font-medium">{category.label}</Label>
                    {category.required && (
                      <Badge variant="secondary" className="text-xs">Required</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {category.description}
                  </p>
                  {category.disabled && category.disabledReason && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      {category.disabledReason}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Examples:</span> {category.examples}
                  </p>
                </div>
                <Switch
                  checked={localPreferences[category.key] as boolean}
                  onCheckedChange={() => togglePreference(category.key)}
                  disabled={category.required || category.disabled}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
          <ExternalLink className="h-3 w-3" />
          <span>
            Learn more in our{" "}
            <a href="/privacy-policy" className="underline hover:text-primary">
              Privacy Policy
            </a>{" "}
            and{" "}
            <a href="/terms-of-service" className="underline hover:text-primary">
              Terms of Service
            </a>
          </span>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
